import { Inject, Injectable } from '@angular/core';
import {
  createConversationUUID,
  ExpyChatSidebar,
  ExpyChatSidebarOptions,
  PersistenceType,
} from '@wk/fab-expy-sdk';
import { WkAuthenticationService } from '../../services/wk-authentication.service';
import { WkAppConfigService } from '../../services/wk-app-config.service';
import { WINDOW_TOKEN } from '../shared/window.token';
import { EXPY_SIDEBAR_CLASS } from './expy-sidebar.token';
import { CommonConstants } from '../../shared';

@Injectable({
  providedIn: 'root',
})
export class WkFabExpyConfigService {
  constructor(
    private readonly authService: WkAuthenticationService,
    private readonly appConfigService: WkAppConfigService,
    @Inject(WINDOW_TOKEN) private readonly _window: Window,
    @Inject(EXPY_SIDEBAR_CLASS)
    private readonly expySideBar: typeof ExpyChatSidebar
  ) {}

  getExpyChatSidebar(
    siteId: string,
    selectedDocuments: Array<{ id: string; title: string; url?: string }> = []
  ): ExpyChatSidebar {
    return new this.expySideBar(
      this.getExpyChatSidebarConfig(siteId, selectedDocuments)
    );
  }

  private getExpyChatSidebarConfig(
    siteId: string,
    selectedDocuments: Array<{ id: string; title: string; url?: string }> = []
  ): ExpyChatSidebarOptions {
    const agentName = this.appConfigService.get('expyAgentName');
    return {
      proxy: {
        baseUrl: `${this._window.location.origin}/alfresco/s/wk/core/ai/${agentName}/${siteId}`,
        requestInterceptor: (options) => {
          const newOptions = JSON.parse(JSON.stringify(options));

          if (!newOptions.init) {
            return Promise.resolve(newOptions);
          }

          if (this.authService.isSsoLoggedIn()) {
            return Promise.resolve(newOptions);
          }

          const token = this.authService.getContentClientBasicAuthToken();
          newOptions.init.headers['Authorization'] = `Basic ${token}`;
          return Promise.resolve(newOptions);
        },
      },
      chat: {
        conversationId: createConversationUUID(),
        uiConfig: {
          selectedDocuments: {
            enabled: true,
            removeSuggestionsAsPerContext: false,
            allowUserToRemoveDocuments: true,
            greeting: {
              icon: {
                enabled: true,
                type: 'source',
                icon: 'document',
                height: 24,
              },
            },
            selectedDocuments,
            mapSelectedDocumentsToContext: (context, documentEntries) => {
              context.contentFilters = [
                {
                  filterType: CommonConstants.EXPY.FILTER.COMPLEX,
                  filters: documentEntries.map(({ url }) => ({
                    filterType: CommonConstants.EXPY.FILTER.PREFIX,
                    fieldName: CommonConstants.EXPY.FILTER.FIELD_NAME,
                    prefix: url,
                  })),
                  operator: CommonConstants.EXPY.FILTER.OPERATORS.OR,
                },
              ];
              context.selectedDocuments = documentEntries;
              return context;
            },
            // @ts-ignore: mapContextToSelectedDocuments is not yet typed in SelectedDocumentsConfig
            mapContextToSelectedDocuments: (context) => {
              const prefixes: string[] = [];

              context.contentFilters?.forEach((filter) => {
                if (filter.filterType === 'Complex' && filter.filters) {
                  filter.filters.forEach((innerFilter: any) => {
                    if (
                      innerFilter.filterType === 'Prefix' &&
                      innerFilter.fieldName === 'tenantid.keyword'
                    ) {
                      prefixes.push(innerFilter.prefix);
                    }
                  });
                }
              });

              return selectedDocuments.filter((doc) =>
                prefixes.some(
                  (prefix) => doc.url === prefix || doc.id === prefix
                )
              );
            },
          },
          historyManager: {
            enabled: true,
            expanded: false,
            width: 400,
          },
          clipboard: {
            enabled: true,
          },
          feedback: {
            enabled: true,
            feedbackOptions: [],
            showConfirmationAfterFeedback: false,
          },
          enableTrackEvents: true,
          liveSuggestions: {
            enabled: true,
            staticSuggestions: [],
            initialSuggestions: [],
          },
          messages: {
            // @ts-ignore: enableThinking is not yet typed in MessagesConfig
            enableThinking: true,
            loading: { type: 'dots' },
            sources: {
              enabled: true,
              hasPrefix: false,
              hasLabel: false,
              numerated: false,
              redirectOnClick: false,
            },
          },
        },
        onEvent: (e) => {
          if (e.event !== CommonConstants.EXPY.EVENTS.CHAT_SOURCE_CLICKED) {
            return;
          }
          let {
            sourceClicked: { url },
          } = e;
          if (!this.authService.isSsoLoggedIn()) {
            url = `${url}&alf_ticket=${this.authService.getTicketEcm()}`;
          }
          this._window.open(url, '_blank');
        },
      },
      agent: {
        expyId: '',
        source: '',
        persistenceType: PersistenceType.BROWSER,
      },
      sidebar: {
        width: '700px',
      },
    };
  }
}
